(() => {
  if (window.KalmixGroundTruthOverallChart) {
    return;
  }

  const COLORS = {
    axis: "#aeb9c4",
    grid: "#e7ecf1",
    text: "#1f2933",
    muted: "#5f6b76",
    horizontal: "#0056b3",
    vertical: "#6d5dfb",
    scenarioBand: "rgba(0, 86, 179, 0.055)",
    scenarioText: "#0056b3",
    crosshair: "rgba(31, 41, 51, 0.42)",
    pointFill: "#ffffff"
  };

  const QUALITY_FALLBACK = {
    0: { label: "No Fix", color: "#8A949D" },
    1: { label: "Single", color: "#D99A00" },
    2: { label: "DGNSS", color: "#0E5CB6" },
    4: { label: "RTK Fixed", color: "#22A95A" },
    5: { label: "RTK Float", color: "#24B1B8" },
    6: { label: "DR", color: "#EC7427" }
  };

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function formatElapsed(seconds) {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;

    return [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(secs).padStart(2, "0")
    ].join(":");
  }

  function formatError(value) {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
      return "—";
    }

    return `${numeric.toFixed(3)} m`;
  }

  function formatPercent(value) {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
      return "—";
    }

    return `${numeric.toFixed(3).replace(/\.?0+$/, "")}%`;
  }

  function formatEpochs(value) {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
      return "—";
    }

    return new Intl.NumberFormat("en-US").format(numeric);
  }

  function accuracyBucket(horizontalError) {
    const value = Number(horizontalError);

    if (!Number.isFinite(value)) {
      return "—";
    }

    if (value < 0.1) {
      return "<10 cm";
    }

    if (value < 1) {
      return "10 cm–1 m";
    }

    if (value < 3) {
      return "1–3 m";
    }

    if (value < 10) {
      return "3–10 m";
    }

    return ">10 m";
  }

  function getColumnIndex(columns, name) {
    const index = columns.indexOf(name);

    if (index < 0) {
      throw new Error(`Missing required chart column: ${name}`);
    }

    return index;
  }

  function getNearestIndex(points, elapsedIndex, elapsed) {
    let left = 0;
    let right = points.length - 1;

    while (left <= right) {
      const middle = Math.floor((left + right) / 2);
      const value = Number(points[middle][elapsedIndex]);

      if (value === elapsed) {
        return middle;
      }

      if (value < elapsed) {
        left = middle + 1;
      } else {
        right = middle - 1;
      }
    }

    const before = Math.max(0, right);
    const after = Math.min(points.length - 1, left);

    const beforeDistance = Math.abs(Number(points[before][elapsedIndex]) - elapsed);
    const afterDistance = Math.abs(Number(points[after][elapsedIndex]) - elapsed);

    return beforeDistance <= afterDistance ? before : after;
  }

  function drawLine(context, points, elapsedIndex, valueIndex, mapX, mapY, panel, color) {
    context.save();
    context.beginPath();
    context.rect(panel.left, panel.top, panel.width, panel.height);
    context.clip();

    context.beginPath();
    context.strokeStyle = color;
    context.lineWidth = 1.65;
    context.lineJoin = "round";
    context.lineCap = "round";

    points.forEach((point, index) => {
      const x = mapX(Number(point[elapsedIndex]));
      const y = mapY(Number(point[valueIndex]));

      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    });

    context.stroke();
    context.restore();
  }

  function drawPanelGrid(context, panel, ticks, mapY, label) {
    context.save();
    context.font = "600 11px Archivo, Arial, sans-serif";
    context.textBaseline = "middle";
    context.fillStyle = COLORS.muted;

    ticks.forEach((tick) => {
      const y = mapY(tick);

      context.strokeStyle = COLORS.grid;
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(panel.left, y);
      context.lineTo(panel.left + panel.width, y);
      context.stroke();

      context.textAlign = "right";
      context.fillText(`${tick} m`, panel.left - 10, y);
    });

    context.strokeStyle = COLORS.axis;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(panel.left, panel.top);
    context.lineTo(panel.left, panel.top + panel.height);
    context.stroke();

    context.font = "700 12px Archivo, Arial, sans-serif";
    context.textAlign = "left";
    context.textBaseline = "alphabetic";
    context.fillStyle = COLORS.text;
    context.fillText(label, panel.left, panel.top - 10);

    context.restore();
  }

  function drawScenarioBands(context, scenarios, panelTop, panelBottom, mapX) {
    context.save();
    context.font = "700 10px Archivo, Arial, sans-serif";
    context.textBaseline = "top";

    scenarios.forEach((scenario) => {
      const start = Number(scenario.start_s);
      const end = Number(scenario.end_s);

      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return;
      }

      const xStart = mapX(start);
      const xEnd = mapX(end);
      const width = Math.max(1, xEnd - xStart);

      context.fillStyle = COLORS.scenarioBand;
      context.fillRect(xStart, panelTop, width, panelBottom - panelTop);

      context.fillStyle = COLORS.scenarioText;
      context.fillText(String(scenario.id || ""), xStart + 5, panelTop + 5);
    });

    context.restore();
  }

  function drawStatusStrip(context, points, elapsedIndex, qualityIndex, mapX, strip) {
    if (!points.length) {
      return;
    }

    let runStart = 0;
    let currentQuality = Number(points[0][qualityIndex]);

    for (let index = 1; index <= points.length; index += 1) {
      const nextQuality = index < points.length
        ? Number(points[index][qualityIndex])
        : null;

      if (index < points.length && nextQuality === currentQuality) {
        continue;
      }

      const startElapsed = Number(points[runStart][elapsedIndex]);
      const endElapsed = index < points.length
        ? Number(points[index][elapsedIndex])
        : Number(points[points.length - 1][elapsedIndex]) + 1;

      const xStart = mapX(startElapsed);
      const xEnd = mapX(endElapsed);
      const color = (QUALITY_FALLBACK[currentQuality] || QUALITY_FALLBACK[0]).color;

      context.fillStyle = color;
      context.fillRect(xStart, strip.top, Math.max(1, xEnd - xStart), strip.height);

      runStart = index;
      currentQuality = nextQuality;
    }

    context.strokeStyle = "#d8e0e7";
    context.lineWidth = 1;
    context.strokeRect(strip.left, strip.top, strip.width, strip.height);
  }

  function validateChartData(data) {
    if (!data || typeof data !== "object") {
      throw new Error("Interactive chart data is not a valid JSON object.");
    }

    if (!Array.isArray(data.columns) || !Array.isArray(data.points) || !data.points.length) {
      throw new Error("Interactive chart data has no usable time-series points.");
    }

    [
      "elapsed_s",
      "horizontal_error_m",
      "vertical_error_m",
      "solution_quality",
      "scenario_code"
    ].forEach((column) => getColumnIndex(data.columns, column));

    return data;
  }

  function initializeMetricValues(section) {
    section.querySelectorAll("[data-gt-metric-value]").forEach((element) => {
      const value = Number(element.dataset.gtMetricValue);

      if (Number.isFinite(value)) {
        element.textContent = value.toFixed(3);
      }
    });
  }

  function initializeDistributionTooltip(section) {
    const module = section.querySelector("[data-gt-distribution-module]");

    if (!module || module.dataset.initialized === "true") {
      return;
    }

    module.dataset.initialized = "true";

    const tooltip = module.querySelector("[data-gt-distribution-tooltip]");
    const segments = module.querySelectorAll("[data-gt-distribution-segment]");

    function hideTooltip() {
      tooltip.classList.remove("is-visible");
    }

    function showTooltip(segment) {
      const kind = segment.dataset.kind || "";
      const label = segment.dataset.label || "";
      const percent = segment.dataset.percent || "";
      const epochs = segment.dataset.epochs || "";

      tooltip.innerHTML = `
        <div class="gt-overall__distribution-tooltip-kind">${escapeHtml(kind)}</div>
        <div class="gt-overall__distribution-tooltip-title">${escapeHtml(label)}</div>
        <div class="gt-overall__distribution-tooltip-meta">
          <span>${escapeHtml(formatPercent(percent))}</span>
          <span>${escapeHtml(formatEpochs(epochs))} epochs</span>
        </div>
      `;

      const moduleRect = module.getBoundingClientRect();
      const segmentRect = segment.getBoundingClientRect();
      const tooltipWidth = tooltip.offsetWidth || 190;
      const tooltipHeight = tooltip.offsetHeight || 72;

      let left = segmentRect.left - moduleRect.left + (segmentRect.width / 2) - (tooltipWidth / 2);
      let top = segmentRect.top - moduleRect.top - tooltipHeight - 10;

      left = Math.max(12, Math.min(left, module.clientWidth - tooltipWidth - 12));

      if (top < 12) {
        top = segmentRect.bottom - moduleRect.top + 10;
      }

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      tooltip.classList.add("is-visible");
    }

    segments.forEach((segment) => {
      segment.addEventListener("pointerenter", () => showTooltip(segment));
      segment.addEventListener("focus", () => showTooltip(segment));
      segment.addEventListener("pointerleave", hideTooltip);
      segment.addEventListener("blur", hideTooltip);

      segment.addEventListener("click", (event) => {
        event.preventDefault();
        showTooltip(segment);
      });
    });
  }

  function createController(root, data) {
    const canvas = root.querySelector("[data-gt-overall-canvas]");
    const stage = root.querySelector("[data-gt-overall-stage]");
    const tooltip = root.querySelector("[data-gt-overall-tooltip]");
    const errorMessage = root.querySelector("[data-gt-overall-error]");

    const context = canvas.getContext("2d");
    const columns = data.columns;
    const points = data.points;
    const axes = data.axes || {};
    const qualityMap = { ...QUALITY_FALLBACK, ...(data.quality_map || {}) };
    const scenarios = Array.isArray(data.scenarios) ? data.scenarios : [];

    const elapsedIndex = getColumnIndex(columns, "elapsed_s");
    const horizontalIndex = getColumnIndex(columns, "horizontal_error_m");
    const verticalIndex = getColumnIndex(columns, "vertical_error_m");
    const qualityIndex = getColumnIndex(columns, "solution_quality");
    const scenarioIndex = getColumnIndex(columns, "scenario_code");

    const minElapsed = Number(points[0][elapsedIndex]);
    const maxElapsed = Number(points[points.length - 1][elapsedIndex]);

    const hAxis = {
      min: Number(axes.horizontal_error_m?.min ?? 0),
      max: Number(axes.horizontal_error_m?.max ?? 30)
    };

    const vAxis = {
      min: Number(axes.vertical_error_m?.min ?? -5),
      max: Number(axes.vertical_error_m?.max ?? 5)
    };

    let hoverIndex = null;
    let dimensions = null;
    let resizeObserver = null;

    function layout() {
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(320, rect.width);
      const height = Math.max(420, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);

      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);

      const mobile = width < 640;
      const left = mobile ? 57 : 68;
      const right = mobile ? 16 : 24;
      const top = 32;
      const bottom = mobile ? 52 : 48;
      const plotWidth = width - left - right;
      const titleGap = 22;
      const panelGap = mobile ? 38 : 44;
      const statusGap = 18;
      const stripHeight = 12;
      const usableHeight = height - top - bottom - titleGap - panelGap - statusGap - stripHeight;
      const horizontalHeight = usableHeight * 0.56;
      const verticalHeight = usableHeight - horizontalHeight;

      const horizontalPanel = {
        left,
        top: top + titleGap,
        width: plotWidth,
        height: horizontalHeight
      };

      const verticalPanel = {
        left,
        top: horizontalPanel.top + horizontalPanel.height + panelGap,
        width: plotWidth,
        height: verticalHeight
      };

      const strip = {
        left,
        top: verticalPanel.top + verticalPanel.height + statusGap,
        width: plotWidth,
        height: stripHeight
      };

      dimensions = {
        width,
        height,
        horizontalPanel,
        verticalPanel,
        strip
      };
    }

    function mapX(elapsed) {
      const { horizontalPanel } = dimensions;
      const range = Math.max(1, maxElapsed - minElapsed);

      return horizontalPanel.left
        + ((elapsed - minElapsed) / range) * horizontalPanel.width;
    }

    function mapY(value, panel, axis) {
      const range = Math.max(0.0001, axis.max - axis.min);
      const bounded = Math.max(axis.min, Math.min(axis.max, value));

      return panel.top + panel.height
        - ((bounded - axis.min) / range) * panel.height;
    }

    function drawXAxis() {
      const { horizontalPanel, strip } = dimensions;
      const tickCount = 5;

      context.save();
      context.font = "600 11px IBM Plex Mono, ui-monospace, monospace";
      context.fillStyle = COLORS.muted;
      context.textAlign = "center";
      context.textBaseline = "top";

      for (let index = 0; index <= tickCount; index += 1) {
        const ratio = index / tickCount;
        const x = horizontalPanel.left + ratio * horizontalPanel.width;
        const elapsed = minElapsed + ratio * (maxElapsed - minElapsed);

        context.strokeStyle = "#dce4ea";
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(x, strip.top + strip.height);
        context.lineTo(x, strip.top + strip.height + 4);
        context.stroke();

        context.fillText(formatElapsed(elapsed), x, strip.top + strip.height + 8);
      }

      context.font = "700 11px Archivo, Arial, sans-serif";
      context.fillStyle = COLORS.muted;
      context.textAlign = "right";
      context.fillText("Elapsed time", horizontalPanel.left + horizontalPanel.width, strip.top + strip.height + 31);

      context.restore();
    }

    function drawHover() {
      if (hoverIndex === null || !points[hoverIndex]) {
        return;
      }

      const point = points[hoverIndex];
      const elapsed = Number(point[elapsedIndex]);
      const horizontal = Number(point[horizontalIndex]);
      const vertical = Number(point[verticalIndex]);

      const x = mapX(elapsed);
      const hY = mapY(horizontal, dimensions.horizontalPanel, hAxis);
      const vY = mapY(vertical, dimensions.verticalPanel, vAxis);

      context.save();

      context.strokeStyle = COLORS.crosshair;
      context.lineWidth = 1;
      context.setLineDash([4, 4]);
      context.beginPath();
      context.moveTo(x, dimensions.horizontalPanel.top);
      context.lineTo(x, dimensions.strip.top + dimensions.strip.height);
      context.stroke();
      context.setLineDash([]);

      [
        { x, y: hY, color: COLORS.horizontal },
        { x, y: vY, color: COLORS.vertical }
      ].forEach((marker) => {
        context.fillStyle = COLORS.pointFill;
        context.strokeStyle = marker.color;
        context.lineWidth = 2;
        context.beginPath();
        context.arc(marker.x, marker.y, 4.2, 0, Math.PI * 2);
        context.fill();
        context.stroke();
      });

      context.restore();
    }

    function render() {
      layout();

      const {
        width,
        height,
        horizontalPanel,
        verticalPanel,
        strip
      } = dimensions;

      context.clearRect(0, 0, width, height);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);

      drawScenarioBands(
        context,
        scenarios,
        horizontalPanel.top,
        verticalPanel.top + verticalPanel.height,
        mapX
      );

      drawPanelGrid(
        context,
        horizontalPanel,
        [0, 10, 20, 30],
        (value) => mapY(value, horizontalPanel, hAxis),
        "Horizontal Error Magnitude"
      );

      drawPanelGrid(
        context,
        verticalPanel,
        [-5, -2.5, 0, 2.5, 5],
        (value) => mapY(value, verticalPanel, vAxis),
        "Vertical Error (Signed)"
      );

      drawLine(
        context,
        points,
        elapsedIndex,
        horizontalIndex,
        mapX,
        (value) => mapY(value, horizontalPanel, hAxis),
        horizontalPanel,
        COLORS.horizontal
      );

      drawLine(
        context,
        points,
        elapsedIndex,
        verticalIndex,
        mapX,
        (value) => mapY(value, verticalPanel, vAxis),
        verticalPanel,
        COLORS.vertical
      );

      drawStatusStrip(
        context,
        points,
        elapsedIndex,
        qualityIndex,
        mapX,
        strip
      );

      drawXAxis();
      drawHover();
    }

    function hideTooltip() {
      tooltip.classList.remove("is-visible");
      hoverIndex = null;
      render();
    }

    function showTooltip(index, pointerX) {
      const point = points[index];
      const elapsed = Number(point[elapsedIndex]);
      const horizontal = Number(point[horizontalIndex]);
      const vertical = Number(point[verticalIndex]);
      const qualityCode = Number(point[qualityIndex]);
      const scenarioCode = Number(point[scenarioIndex]);

      const quality = qualityMap[qualityCode] || QUALITY_FALLBACK[0];
      const scenario = scenarios.find((item) => Number(item.code) === scenarioCode);

      tooltip.innerHTML = `
        <div class="gt-overall__tooltip-time">${escapeHtml(formatElapsed(elapsed))}</div>
        <div class="gt-overall__tooltip-row">
          <span class="gt-overall__tooltip-key">Horizontal</span>
          <span class="gt-overall__tooltip-value">${escapeHtml(formatError(horizontal))}</span>
        </div>
        <div class="gt-overall__tooltip-row">
          <span class="gt-overall__tooltip-key">Vertical</span>
          <span class="gt-overall__tooltip-value">${escapeHtml(formatError(vertical))}</span>
        </div>
        <div class="gt-overall__tooltip-row">
          <span class="gt-overall__tooltip-key">Quality</span>
          <span class="gt-overall__tooltip-value">${escapeHtml(quality.label)}</span>
        </div>
        <div class="gt-overall__tooltip-row">
          <span class="gt-overall__tooltip-key">Accuracy</span>
          <span class="gt-overall__tooltip-value">${escapeHtml(accuracyBucket(horizontal))}</span>
        </div>
        <div class="gt-overall__tooltip-row">
          <span class="gt-overall__tooltip-key">Scenario</span>
          <span class="gt-overall__tooltip-value">${escapeHtml(scenario?.id || "—")}</span>
        </div>
      `;

      const stageWidth = dimensions.width;
      const tooltipWidth = 228;
      const nextLeft = pointerX + 16;
      const left = nextLeft + tooltipWidth > stageWidth
        ? Math.max(10, pointerX - tooltipWidth - 16)
        : nextLeft;

      tooltip.style.left = `${left}px`;
      tooltip.style.top = "14px";
      tooltip.classList.add("is-visible");
    }

    function handlePointerMove(event) {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const { horizontalPanel } = dimensions;

      if (
        x < horizontalPanel.left
        || x > horizontalPanel.left + horizontalPanel.width
      ) {
        hideTooltip();
        return;
      }

      const ratio = (x - horizontalPanel.left) / horizontalPanel.width;
      const elapsed = minElapsed + ratio * (maxElapsed - minElapsed);

      hoverIndex = getNearestIndex(points, elapsedIndex, elapsed);
      render();
      showTooltip(hoverIndex, x);
    }

    function handlePointerLeave() {
      hideTooltip();
    }

    function mount() {
      canvas.addEventListener("pointermove", handlePointerMove);
      canvas.addEventListener("pointerleave", handlePointerLeave);

      resizeObserver = new ResizeObserver(() => {
        render();
      });

      resizeObserver.observe(canvas);
      render();
      stage.classList.add("is-ready");
    }

    function destroy() {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    }

    return {
      mount,
      render,
      destroy
    };
  }

  function initializeChart(chartRoot) {
    if (!chartRoot || chartRoot.dataset.initialized === "true") {
      chartRoot?._gtOverallController?.render();
      return;
    }

    const stage = chartRoot.querySelector("[data-gt-overall-stage]");
    const errorMessage = chartRoot.querySelector("[data-gt-overall-error]");
    const url = chartRoot.dataset.chartUrl;

    if (!url) {
      stage.classList.add("is-error");
      errorMessage.textContent = "Interactive chart data has not been linked to this result.";
      return;
    }

    chartRoot.dataset.initialized = "true";

    fetch(url, {
      credentials: "omit",
      cache: "force-cache"
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load chart data (${response.status}).`);
        }

        return response.json();
      })
      .then((payload) => {
        const data = validateChartData(payload);
        const controller = createController(chartRoot, data);

        chartRoot._gtOverallController = controller;
        controller.mount();
      })
      .catch((error) => {
        stage.classList.add("is-error");
        errorMessage.textContent = `Unable to render interactive chart: ${error.message}`;
      });
  }

  function activatePanel(section, targetId) {
    const panels = section.querySelectorAll("[data-gt-result-panel]");
    const tabs = section.querySelectorAll("[data-gt-result-tab]");

    panels.forEach((panel) => {
      const active = panel.id === targetId;
      panel.hidden = !active;

      if (active) {
        const chartRoot = panel.querySelector("[data-gt-overall-chart]");
        initializeChart(chartRoot);

        requestAnimationFrame(() => {
          chartRoot?._gtOverallController?.render();
        });
      }
    });

    tabs.forEach((tab) => {
      const active = tab.dataset.gtResultTarget === targetId;
      tab.setAttribute("aria-selected", active ? "true" : "false");
    });
  }

  function initializeSection(section) {
    if (section.dataset.initialized === "true") {
      return;
    }

    section.dataset.initialized = "true";

    initializeMetricValues(section);
    initializeDistributionTooltip(section);

    const tabs = section.querySelectorAll("[data-gt-result-tab]");
    const firstPanel = section.querySelector("[data-gt-result-panel]");

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        activatePanel(section, tab.dataset.gtResultTarget);
      });
    });

    if (firstPanel) {
      activatePanel(section, firstPanel.id);
    }
  }

  function bootstrap(scope = document) {
    scope.querySelectorAll("[data-gt-overall-section]").forEach(initializeSection);
  }

  window.KalmixGroundTruthOverallChart = {
    bootstrap,
    initializeChart
  };

  document.addEventListener("DOMContentLoaded", () => {
    bootstrap();
  });

  document.addEventListener("shopify:section:load", (event) => {
    bootstrap(event.target);
  });
})();